import { useRef, useEffect, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useIdentity } from 'context/identity-provider';
import { config, experience, education } from 'data/resume';
import ResumeHeader from 'features/resume/components/ResumeHeader';
import ResumeNav from 'features/resume/components/ResumeNav';
import ExperienceSection from 'features/resume/components/ExperienceSection';
import EducationSection from 'features/resume/components/EducationSection';
import SkillsSection from 'features/resume/components/SkillsSection';
import styles from './resume-view.module.scss';

const SECTIONS = [
  { id: 'experience', label: 'Experience' },
  { id: 'skills',     label: 'Skills' },
  { id: 'education',  label: 'Education' },
];

export default function ResumeView() {
  const { isAuth } = useIdentity();
  const [activeSection, setActiveSection] = useState('experience');
  const isScrollingTo = useRef(false);

  const experienceRef = useRef<HTMLDivElement>(null);
  const skillsRef     = useRef<HTMLDivElement>(null);
  const educationRef  = useRef<HTMLDivElement>(null);

  const refMap: Record<string, React.RefObject<HTMLDivElement | null>> = {
    experience: experienceRef,
    skills:     skillsRef,
    education:  educationRef,
  };

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (isScrollingTo.current) return;
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const section = (entry.target as HTMLElement).dataset.section;
        if (section) setActiveSection(section);
      });
    }, { rootMargin: '0px 0px -70% 0px', threshold: 0 });

    Object.values(refMap).forEach(ref => {
      if (ref.current) observer.observe(ref.current);
    });
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToSection = useCallback((id: string) => {
    isScrollingTo.current = true;
    setActiveSection(id);
    refMap[id]?.current?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => { isScrollingTo.current = false; }, 800);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.resumeRoot}>
      <ResumeHeader config={config} />

      <ResumeNav
        sections={SECTIONS}
        activeSection={activeSection}
        onNavigate={scrollToSection}
      />

      <main className={styles.main}>
        <div ref={experienceRef} data-section="experience">
          <ExperienceSection roles={experience} />
        </div>
        <div ref={skillsRef} data-section="skills">
          <SkillsSection skills={config.skills} />
        </div>
        <div ref={educationRef} data-section="education">
          <EducationSection entries={education} />
        </div>
      </main>

      <footer className={styles.footer}>
        {config.name} · {config.location} ·{' '}
        <Link to={isAuth ? '/portal' : '/login'} className={styles.footerLink}>
          {isAuth ? 'portal' : 'login'}
        </Link>
      </footer>
    </div>
  );
}
